if (!customElements.get('wls-video')) {
  class WLSVideo extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        playButton: 'js-wls-video-play-button',
        playing: 'playing',
      };

      this.videos = this.querySelectorAll('video');
      this.selectors = this.getSelectors();
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        threshold: 0.5,
      });
    }

    /**
     * @returns {Object}
     * @description Get selectors
     */
    getSelectors() {
      return {
        playButton: this.querySelector(`.${this.classes.playButton}`),
      };
    }

    /**
     * @description Vrati trenutno vidljiv video element
     */
    getVisibleVideo() {
      return Array.from(this.videos).find((video) => getComputedStyle(video).display !== 'none');
    }

    /**
     * @description Add event listeners and start observing the video
     */
    connectedCallback() {
      const video = this.getVisibleVideo();
      if (video) {
        this.observer.observe(this);

        // Add ended event handler to reset state
        video.addEventListener('ended', this.handleVideoEnded.bind(this));

        // Handle autoplay videos
        if (video.hasAttribute('autoplay')) {
          this.handleAutoplayVideo();
        } else if (this.selectors.playButton) {
          this.selectors.playButton.addEventListener('click', this.videoPlayingHandler.bind(this));
        }
      }
    }

    /**
     * @description Handle autoplay video setup
     */
    handleAutoplayVideo() {
      if (this.selectors.playButton) {
        this.selectors.playButton.style.pointerEvents = 'none';
      }
      const video = this.getVisibleVideo();
      if (video) {
        video.addEventListener('playing', () => {
          this.classList.add(this.classes.playing);
        });
      }
    }

    /**
     * @description Disconnect the observer and remove the event listener
     */
    disconnectedCallback() {
      if (this.selectors.playButton) {
        this.selectors.playButton.removeEventListener('click', this.videoPlayingHandler);
      }
      this.observer.disconnect();
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        const video = this.getVisibleVideo();
        if (video && !entry.isIntersecting && !video.paused) {
          this.videoPause();
        }
      });
    }

    /**
     * @description Pause all other synchronized videos
     */
    pauseSyncedVideos() {
      if (this.hasAttribute('data-sync-pause')) {
        const allSyncedVideos = document.querySelectorAll('wls-video[data-sync-pause]');
        allSyncedVideos.forEach((videoElement) => {
          if (videoElement !== this) {
            const v = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
            if (v && !v.paused) {
              videoElement.classList.remove(this.classes.playing);
              v.pause();
            }
          }
        });
      }
    }

    /**
     * @description Handle video playing and pausing
     */
    videoPlayingHandler() {
      const video = this.getVisibleVideo();
      if (!video) return;
      if (video.paused) {
        this.pauseSyncedVideos();
        this.videoPlay();
      } else {
        this.videoPause();
      }
    }

    /**
     * @description Play the video
     */
    videoPlay() {
      const video = this.getVisibleVideo();
      if (video) {
        video.play();
        this.classList.add(this.classes.playing);
      }
    }

    /**
     * @description Pause the video
     */
    videoPause() {
      const video = this.getVisibleVideo();
      if (video && !video.hasAttribute('autoplay')) {
        video.pause();
        this.classList.remove(this.classes.playing);
      }
    }

    /**
     * @description Handle video ended event, reset to initial state
     */
    handleVideoEnded() {
      this.classList.remove(this.classes.playing);
    }
  }

  customElements.define('wls-video', WLSVideo);
}
